/**
 * Pure helpers for Lazada category list / suggestion / attribute API envelopes.
 */

export interface LazadaCategoryOptionSource {
  categoryId?: number;
  name?: string;
  categoryName?: string;
}

export interface LazadaCategorySuggestionLike {
  categoryId?: number;
  categoryName?: string;
}

/** Extract first array from common API wrapper shapes. */
export function coerceArrayFromApiEnvelope(payload: unknown): unknown[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];
  const o = payload as Record<string, unknown>;
  const keys = ['data', 'items', 'content', 'result', 'body'];
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  for (const k of Object.keys(o)) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function mapLazadaCategorySourcesToOptions(
  list: ReadonlyArray<LazadaCategoryOptionSource>,
): Array<{ label: string; value: string }> {
  // Marketplaces (Shopee/Lazada) accept only the deepest (leaf) category. The list endpoint
  // returns a nested tree (each node may carry `children`), so flatten to leaf-only options and
  // label each with its full path ("Parent > Child > Leaf"). A node with no non-empty `children`
  // is a leaf — the `leaf` flag is ignored because Shopee marks some parents leaf:true.
  // Flat lists (no children) collapse to the node itself, so Lazada behaviour is unchanged.
  const out: Array<{ label: string; value: string }> = [];
  const walk = (nodes: ReadonlyArray<LazadaCategoryOptionSource>, path: string[]): void => {
    for (const item of nodes ?? []) {
      const name = String(item?.name ?? item?.categoryName ?? '');
      const children = Array.isArray((item as { children?: unknown[] })?.children)
        ? ((item as { children: LazadaCategoryOptionSource[] }).children)
        : [];
      const nextPath = name ? [...path, name] : path;
      if (children.length === 0) {
        out.push({ label: nextPath.join(' > '), value: String(item?.categoryId ?? '') });
      } else {
        walk(children, nextPath);
      }
    }
  };
  walk(list, []);
  return out;
}

export function mapLazadaSuggestionSourcesToOptions(
  list: ReadonlyArray<LazadaCategorySuggestionLike>,
): Array<{ label: string; value: string }> {
  return list.map((s) => ({
    label: String(s.categoryName ?? ''),
    value: String(s.categoryId ?? ''),
  }));
}

export function mapLazadaCategoryListResponseToOptions(
  response: unknown,
): Array<{ label: string; value: string }> {
  return mapLazadaCategorySourcesToOptions(
    coerceArrayFromApiEnvelope(response) as LazadaCategoryOptionSource[],
  );
}

export function mapLazadaCategorySuggestionResponseToOptions(
  response: unknown,
): Array<{ label: string; value: string }> {
  return mapLazadaSuggestionSourcesToOptions(
    coerceArrayFromApiEnvelope(response) as LazadaCategorySuggestionLike[],
  );
}

/** Category attribute API body may use attribute | attributes | standard arrays. */
export function extractLazadaCategoryAttributesFromBody(
  body: unknown,
): unknown[] {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  if (Array.isArray(o['attribute'])) return o['attribute'] as unknown[];
  if (Array.isArray(o['attributes'])) return o['attributes'] as unknown[];
  const keys = ['data', 'items', 'content', 'result'];
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  for (const k of Object.keys(o)) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function partitionLazadaAttributesByMandatory<
  T extends { isMandatory?: number },
>(attributes: T[]): { required: T[]; optional: T[] } {
  const required: T[] = [];
  const optional: T[] = [];
  for (const attr of attributes) {
    if (attr.isMandatory === 1) {
      required.push(attr);
    } else {
      optional.push(attr);
    }
  }
  return { required, optional };
}
